import React, {useCallback} from 'react';
import {useDispatch} from 'react-redux';
import {Image, TouchableOpacity, View, Text, FlatList} from 'react-native';

import useWindowDimensionsSafe from 'src/Modules/Native/useWindowDimensionsSafe';

import {BodyText} from 'src/Modules/CommonView';

import {setCurrentHunt} from 'src/Redux/reducers/hunts.reducer.js';
import {parseHtmlEntities} from 'src/Utils/helpers';
import StarRating from 'src/Modules/Native/StarRating';

import UserIcon from 'src/HeaderComponents/UserIcon';
import Stars from './Stars';
import {navigationRef} from 'src/Nav/navigationHelpers';
import {cloudFlareOptimizeImage} from '../../Utils/helpers';
import {logCustomAnalyticsEvent} from '../../Utils/fireUtils';

// dinner plate empty white
const fallbackImg = {
  uri: cloudFlareOptimizeImage(
    'https://photos.letsroam.com/photos_other/scavenger-hunt-location-1648578804_medium.jpg',
  ),
};

const CarouselView = ({
  data = [],
  handleSnapToMap,
  renderWidth = 240,
  dataSource,
  compact = false,
  cardPress,
  showStars,
  carouselIndex = 0,
  type = 'offer_card',
}) => {
  const {width} = useWindowDimensionsSafe();
  const dispatch = useDispatch();

  const calculateFormatWalkingTime = (distanceMiles) => {
    if (!distanceMiles || isNaN(distanceMiles)) {
      return null;
    }
    if (distanceMiles < 2) {
      // based on avg of 10 minutes to walk 0.6 miles
      const walkingTime = Math?.floor((distanceMiles / 0.6) * 10);
      return `${walkingTime} min walk`;
    } else {
      return `${distanceMiles} miles away`;
    }
  };

  const _renderCustomCard = ({item, index}) => {
    const customCard = dataSource?.[item] || (item?.name ? item : {}) || {};
    const cardHeight = 300;
    // console.log('the item is ', {customCard});

    return (
      <CarouselCard
        imgSource={customCard?.image || fallbackImg}
        title={customCard?.name}
        description={customCard?.description}
        leftText={' '}
        testID={`customCard${index}`}
        rightText={customCard?.rightText || 'Learn More'}
        handlePress={customCard?.handlePress}
        cardWidth={renderWidth}
        cardHeight={cardHeight}
      />
    );
  };

  const _renderGroupCard = ({item}) => {
    const cardHeight = 120;

    return (
      <GroupCard
        groupInfo={item}
        handlePress={item?.handlePress}
        cardWidth={renderWidth}
        cardHeight={cardHeight}
      />
    );
  };

  const _renderGuestBook = ({item, index}) => {
    const location = {...item};
    location.description = location.guide_description;

    return (
      <View
        nativeID={`guestCard${index}`}
        style={{
          backgroundColor: 'white',
          width: width - 140,
          marginRight: 20,
          minHeight: 200,
          marginVertical: 10,
          marginHorizontal: 10,
          borderRadius: 10,
          display: 'flex',
          padding: 10,
          // flexDirection: 'row',

          // padding: 10,
        }}>
        <View style={{flexDirection: 'row', alignItems: 'center'}}>
          <UserIcon
            hideBadges
            disableSignIn
            userPhoto={item?.submitted_by_user_image}
            containerStyle={{width: 40}}
          />
          <Text
            style={{
              backgroundColor: 'white',
              color: global.color.orange,
              fontSize: 16,
            }}>
            {item?.submitted_by_user_name}
          </Text>
        </View>
        <Text style={{backgroundColor: 'white'}}>{item?.content}</Text>
      </View>
    );
  };

  const _renderLocalCard = ({item, index}) => {
    const location = {...item};
    location.description = location.guide_description;
    const cardHeight = compact ? 130 : 300;
    // console.log({location});
    var location_photo = !!location.photo
      ? {
          uri: cloudFlareOptimizeImage(
            location.photo?.replace?.('./', 'https://photos.letsroam.com/'),
          ),
        }
      : fallbackImg;

    const displayedReviews = Math.max(
      location.total_reviews,
      (location.total_reviews % 14) + 12 + (location.hunt_id % 14),
    );

    var description = parseHtmlEntities(
      location.guide_description || location.guide_short_description,
    );

    return (
      <CarouselCard
        imgSource={location_photo}
        title={location.name}
        testID={`localCard-${carouselIndex}-${index}`}
        description={description}
        compact={compact}
        leftText={`${location.distanceAway} miles away`}
        handlePress={() => cardPress(item)}
        cardWidth={renderWidth}
        cardHeight={cardHeight}
        total_reviews={displayedReviews}
        star_rating={location.star_rating}
        rightText={showStars ? <Stars rating={location.rating} /> : null}
      />
    );
  };

  const _renderHuntCard = ({item, index}) => {
    const location = item;
    const cardHeight = compact ? 130 : 300;
    // console.log({location});
    var location_photo = !!location.huntMediumPhotoURL
      ? {
          uri: cloudFlareOptimizeImage(location.huntMediumPhotoURL),
        }
      : fallbackImg;

    const displayedReviews = Math.max(
      location.total_reviews,
      (location.total_reviews % 14) + 12 + (location.hunt_id % 14),
    );

    var description = parseHtmlEntities(
      location.description || location.long_description,
    )?.replace(/<\/?[^>]+(>|$)/g, '');

    // global.devAlert(location.name);

    const isTopRated =
      location?.total_reviews > 1 && location?.star_rating > 4.3;
    return (
      <CarouselCard
        imgSource={location_photo}
        title={location.name || `${location.city} Scavenger Hunt`}
        description={compact ? '' : description}
        compact={compact}
        testID={`huntCard-${carouselIndex}-${index}`}
        leftText={calculateFormatWalkingTime(location.distanceAway)}
        rightText={
          (isTopRated ? 'Top Rated!' : '') +
          (__DEV__ && location.total_reviews > 0
            ? `${location.star_rating}(${location.total_reviews})`
            : '')
        }
        handlePress={() => {
          dispatch(setCurrentHunt(location));
          navigationRef.navigate('ScavengerHuntInfoModal');
          logCustomAnalyticsEvent('Scavenger_hunt_view', location);
        }}
        cardWidth={240}
        cardHeight={cardHeight}
        total_reviews={displayedReviews}
        star_rating={location.star_rating}
        showReviews={true}
      />
    );
  };

  const useRenderFunction = {
    // map_card: _renderMapCarouselCard,
    local_card: _renderLocalCard,
    guest_book: _renderGuestBook,
    // location_card: _renderLocationCard,
    group_card: _renderGroupCard,
    custom_card: _renderCustomCard,
    hunt_card: _renderHuntCard,
    // featured_card: _renderFeaturedCard,
    // offer_card: _renderOfferCard,
  };

  var reducedData = [...(data || [])];

  // console.log({data});
  const onSnapTo = useCallback(({viewableItems}) => {
    if (viewableItems?.length > 0) {
      const {item: activeItem, index} = viewableItems[0];
      console.log('I snapped to', index || '0');
      handleSnapToMap && handleSnapToMap(activeItem);
    }
  }, []);

  return (
    <View
      style={{
        width,
      }}>
      {!!data && (
        <FlatList
          activeSlideAlignment={'start'}
          horizontal
          keyExtractor={(item) =>
            item?.hunt_id || item?.id || item?.name || item?.description
          }
          extraData={data}
          initialNumToRender={2}
          inactiveSlideOpacity={1}
          getItemLayout={(data, index) => ({
            length: 260,
            offset: 260 * index,
            index,
          })}
          snapToAlignment="start"
          snapToStart
          onSn
          snapToInterval={260}
          // decelerationRate={0.8}
          decelerationRate={0.9}
          windowSize={3}
          data={reducedData}
          renderItem={useRenderFunction[type]}
          sliderWidth={width}
          itemWidth={renderWidth + 20}
          removeClippedSubviews={type == 'hunt_card'}
          onViewableItemsChanged={onSnapTo}
        />
      )}
    </View>
  );
};

const GroupCard = ({
  groupInfo,
  handlePress,
  cardWidth = 270,
  cardHeight = 200,
}) => {
  return (
    <TouchableOpacity
      style={{
        backgroundColor: global?.color?.white,
        width: cardWidth,
        minHeight: cardHeight,
        marginVertical: 10,
        marginHorizontal: 10,
        borderRadius: 10,
      }}
      activeOpacity={0.8}
      onPress={handlePress}
      disabled={!handlePress}>
      <Image
        source={
          groupInfo?.huntPhoto ? {uri: groupInfo?.huntPhoto} : fallbackImg
        }
        style={{
          backgroundColor: global?.color?.gray1,
          width: cardWidth,
          height: cardHeight,
          resizeMode: 'cover',
          position: 'absolute',
          borderRadius: 10,
        }}
      />

      <View
        style={{
          flex: 1,
          borderRadius: 10,
          backgroundColor: global?.color?.black_70,
          justifyContent: 'flex-end',
          position: 'relative',
        }}>
        {!!groupInfo?.createdByName && (
          <BodyText
            text={`Join ${groupInfo?.createdByName}'s ${groupInfo?.huntCity} Scavenger Hunt`}
            textStyle={{
              marginVertical: 5,
              fontSize: 20,
              color: global?.color?.gray2,
            }}
          />
        )}
        {!!groupInfo?.distanceAway && (
          <BodyText
            text={`${groupInfo?.distanceAway} Miles Away`}
            textStyle={{
              marginVertical: 5,
              fontSize: 12,
              color: global?.color?.gray2,
            }}
          />
        )}
      </View>
    </TouchableOpacity>
  );
};

export const CarouselCard = ({
  imgSource,
  title = '',
  testID,
  description = '',
  leftText = '',
  rightText = '',
  handlePress,
  cardWidth,
  cardHeight = 240,
  star_rating,
  total_reviews,
  showReviews = false,
  wrap = false,
}) => {
  const {width} = useWindowDimensionsSafe();
  console.log('CarouselCard,' + title + ',' + imgSource?.uri);

  // return <View></View>;
  return (
    <TouchableOpacity
      style={{
        backgroundColor: global?.color?.white,
        width: wrap ? width - 20 : cardWidth,
        minHeight: wrap ? null : cardHeight,
        marginVertical: 10,
        marginHorizontal: 10,
        borderRadius: 10,
      }}
      activeOpacity={0.8}
      onPress={handlePress}
      testID={testID}
      disabled={!handlePress}>
      <View
        style={{
          flex: 1,
          ...(wrap
            ? {
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row',
                backgroundColor: 'white',
                borderRadius: 10,
              }
            : {}),
        }}>
        <Image
          source={imgSource}
          style={{
            backgroundColor: global?.color?.gray1,
            width: wrap ? 120 : cardWidth - 20,
            marginTop: 10,
            marginHorizontal: 10,
            marginBottom: wrap ? 10 : 0,
            height: 140,
            resizeMode: 'cover',
            position: 'relative',
          }}
        />
        <View
          style={{
            position: 'relative',
            width: wrap ? width - 180 : cardWidth,
          }}>
          {!!title && (
            <BodyText
              text={title}
              textStyle={{
                marginVertical: 0,
                color: global?.color?.orange,
                fontSize: 14,
                fontWeight: '700',
              }}
            />
          )}
          {!!showReviews && (
            <View
              style={{
                flexDirection: 'row',
                alignItems: 'flex-start',
                marginLeft: 14,
                marginTop: 5,
                marginBottom: -5,
              }}>
              <StarRating
                startingValue={Math?.max(star_rating, 3.8)}
                continuous={true}
                allowsHalfStars={true}
                accurateHalfStars={true}
                scoreTextStyle={{color: 'transparent'}}
                fullStarColor={global?.color?.yellow}
                imageSize={18}
                readonly
                starStyle={{marginLeft: 2}}
              />
              <Text
                style={{
                  fontSize: 10,
                  color: global?.color?.gray3,
                  fontFamily: global.font.gothic,
                  fontWeight: 'bold',
                  marginLeft: 5,
                }}>
                ({total_reviews})
              </Text>
            </View>
          )}
          {!!description && (
            <BodyText
              text={
                description?.length > 120
                  ? `${description.substring(0, 110)}...`
                  : description
              }
              textStyle={{
                marginVertical: 5,
                fontSize: 14,
              }}
            />
          )}
        </View>
      </View>
      <View style={{flexDirection: 'row', justifyContent: 'space-between'}}>
        {!!leftText && (
          <BodyText
            text={leftText}
            textStyle={{
              marginVertical: 5,
              fontSize: 14,
              color: global?.color?.gray2,
            }}
          />
        )}
        {!!rightText && (
          <BodyText
            text={rightText}
            textStyle={{
              marginVertical: 5,
              fontSize: 14,
              color: global?.color?.gray2,
            }}
          />
        )}
      </View>
    </TouchableOpacity>
  );
};

export default CarouselView;
